'use client';

import { Children, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselOptions,
} from '../ui/carousel';
import { cn } from '@/src/lib/utils/command';
import { useLocale } from 'next-intl';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

interface SliderLayoutProps {
  itemsClassName?: string;
  opts?: Partial<CarouselOptions>;
  children: React.ReactNode;
  setApi?: (api: CarouselApi) => void;
  onSlideChange?: (index: number) => void;
  conatinerClassName?: string;
  plugins?: any[];
}

export const SliderLayout = ({
  itemsClassName,
  opts,
  children,
  setApi,
  onSlideChange,
  conatinerClassName,
  plugins = [],
}: SliderLayoutProps) => {
  const slides = Children.toArray(children);
  const locale = useLocale();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  const wheelGestures = useMemo(
    () =>
      WheelGesturesPlugin({
        forceWheelAxis: 'x',
      }),
    [],
  );

  const handleSetApi = useCallback(
    (api: CarouselApi) => {
      setCarouselApi(api);
      setApi?.(api);
    },
    [setApi],
  );

  useEffect(() => {
    if (!carouselApi || !onSlideChange) return;

    const onSelect = () => {
      onSlideChange(carouselApi.selectedScrollSnap());
    };

    onSelect();
    carouselApi.on('select', onSelect);

    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi, onSlideChange]);

  return (
    <Carousel
      setApi={handleSetApi}
      plugins={[wheelGestures, ...plugins]}
      opts={{
        align: 'start',
        dragFree: true,
        containScroll: 'trimSnaps',
        watchDrag: true,
        direction: locale === 'ar' ? 'rtl' : 'ltr',
        ...opts,
      }}
    >
      <CarouselContent className={cn(conatinerClassName)}>
        {slides.map((slide, index) => (
          <CarouselItem
            key={`slide-${(slide as any).key ?? index}-${index}`}
            className={cn(itemsClassName)}
          >
            {slide}
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
};
